<script setup>
import { NFlex } from 'naive-ui'; 
import { computed } from 'vue';
import RadarChart from './RadarChart.vue'; 

const props = defineProps({
  type: { 
    type: String,
    required: true,
    validator: (value) => ['literacy', 'numeracy'].includes(value)
  }, 
  ourClassPercentages: { 
    type: Array,
    default: () => [],
    validator: (value) => value.every(item => typeof item === 'number' && item >= 0 && item <= 100)
  },
  overallPercentages: { 
    type: Array,
    default: () => [],
    validator: (value) => value.every(item => typeof item === 'number' && item >= 0 && item <= 100)
  }, 
  isStudent: {
    type: Boolean,
    default: false 
  },
  userRole: {
    type: String,
    default: 'teacher', 
    validator: (value) => ['student', 'teacher', 'principal', 'parent'].includes(value)
  }
});

const itemLabels = computed(() => {
  if (props.type === 'literacy') {
    return ['어휘', '탐색 및 확인', '통합 및 해석', '비판 및 평가'];
  } else { 
    return ['수와 양', '규칙과 관계', '모양과 측정', '자료와 불확실성'];
  }
});

// 캔버스 p 태그에 표시할 라벨 배열
const displayItemLabels = computed(() => {
  if (props.type === 'literacy') {
    return [
      '어휘', 
      '탐색 및 확인',
      '통합 및 해석',
      '비판 및 평가'
    ]; 
  } else { 
    return [
      '수와 양', 
      '규칙과 관계',
      '모양과 측정',
      '자료와 불확실성'
    ];
  }
});

const title = computed(() => {
  if (props.isStudent) {    
    return '하위 영역별 정답 비율';
  } else {   
    const subject = props.type === 'literacy' ? '문해력' : '수리력';
    return `${subject} 하위 영역별 정답 비율`;
  }
})

const formattedOurClassRates = computed(() => { 
  return displayItemLabels.value.map((label, index) => {
    const percent = props.ourClassPercentages[index] !== undefined && props.ourClassPercentages[index] !== -1 
      ? props.ourClassPercentages[index] 
      : '0';
    return `${label} : <span>${percent === '0%' ? percent : percent + '%'}</span>`;
  });
});

const formattedOverallRates = computed(() => {
  return displayItemLabels.value.map((label, index) => {
    const percent = props.overallPercentages[index] !== undefined && props.overallPercentages[index] !== -1
      ? props.overallPercentages[index] 
      : '0';
    return `${label} : <span>${percent === '0%' ? percent : percent + '%'}</span>`;
  });
});

const classText = computed(() => {
  if (props.userRole === 'principal') {
    return '우리학교';
  } else if (props.userRole === 'student') {
    return '나의';
  } else {
    return '우리반';
  }
});

// -1 값인지 체크하는 computed
const hasValidData = computed(() => {
  // 자신의 데이터(ourClassPercentages)가 모두 -1인지만 체크
  // 전체 데이터는 항상 있다고 가정
  const ourClassAllInvalid = props.ourClassPercentages.every(val => val === -1);
  
  console.log('hasValidData check:', {
    ourClassPercentages: props.ourClassPercentages,
    ourClassAllInvalid,
    result: !ourClassAllInvalid
  });
  
  // 자신의 데이터가 모두 -1이면 false
  return !ourClassAllInvalid;
});

// RadarChart에 전달할 데이터 (모든 값이 -1이면 빈 배열)
const chartOurClassData = computed(() => {
  return props.ourClassPercentages.every(val => val === -1) ? [] : props.ourClassPercentages;
});

const chartOverallData = computed(() => {
  // 자신의 값이 모두 -1이면 전체 데이터도 함께 숨김
  const ourClassAllInvalid = props.ourClassPercentages.every(val => val === -1);
  const overallAllInvalid = props.overallPercentages.every(val => val === -1);
  
  if (ourClassAllInvalid || overallAllInvalid) {
    return [];
  }
  
  return props.overallPercentages;
});
</script>

<template>
  <div class="card answer" :class="props.type">
    <h5 class="card-title" :class="props.type">{{ title }}</h5>
    <n-flex :wrap="false" :size="40" class="pt-5">
      <!-- 그래프로 교체 -->
      <div class="chart-wrap" style="width: 560px"> 
        <div class="inner">
        <p class="label">{{ displayItemLabels[0] }}</p> 
          <n-flex :wrap="false" :size="0">
            <p class="label">{{ displayItemLabels[1] }}</p> 
            <RadarChart 
              :chart-type="props.type"
              :item-labels="itemLabels"
              :our-class-percentages="chartOurClassData"
              :overall-percentages="chartOverallData"
              :show-values="false"
            />
            <p class="label">{{ displayItemLabels[2] }}</p> 
          </n-flex>
          <p class="label">{{ displayItemLabels[3] }}</p> 
        </div>
      </div>      
      <div class="box" v-if="hasValidData">
        <n-flex justify="start" :size="0">
          <n-flex :wrap="false" :size="24">
            <p>{{ classText }}<br>정답 비율</p>
            <ul>
              <li v-for="(rateHtml, index) in formattedOurClassRates" :key="index" v-html="rateHtml"></li>
            </ul>
          </n-flex>
          <n-flex :wrap="false" :size="22">
            <p>전체<br>정답 비율</p>
            <ul>
              <li v-for="(rateHtml, index) in formattedOverallRates" :key="index" v-html="rateHtml"></li>
            </ul>
          </n-flex>
        </n-flex>       
        <ul class="caution flex flex-col gap-2">
          <li>전체 정답 비율은 본 진단검사에 참여한 표집학교의 해당 학년 학생 전체의 정답 비율을 의미합니다.</li>
          <li>이 그래프는 하위 영역 간 상대적 수행 능력 차이를 의미하지는 않습니다.</li>
        </ul>
      </div>
    </n-flex>
  </div>
</template>

<style lang="scss" scoped>
.box { 
    background-color: #FAFAFA;
    border-radius: 10px;
    flex: 1;
    padding: 23px 38px 23px 40px;
    .n-flex {
        .n-flex {     
            width: 100%;   
            padding-bottom: 24px;
            p { 
                background-color: #fff;
                border-radius: 10px;
                width: 120px;
                height: 110px;
                display: flex;
                align-items: center;
                justify-content: center;
                text-align: center;
                font-size: 16px;
                font-weight: 500;
            }            
            &:nth-child(1) {    
                padding-bottom: 20px;            
                p { 
                    border: 1px solid var(--result-green);
                    color: var(--result-green);
                }
            } 
            &:nth-child(2) {
                padding-top: 20px;
                border-top: 2px dashed #ccc;                
            }
            ul {
                color: #666;

                li {
                    position: relative;   
                    padding-left: 15px;
                    white-space: nowrap;
                    &::before {
                        content: "";
                        position: absolute;
                        width: 3px;
                        height: 3px;
                        top: 50%;
                        left: 0;
                        transform: translateY(-50%);
                        border-radius: 100%;
                        background-color: #666;
                    }     
                    :deep(span) {
                        color: #000;
                        font-weight: 500;
                        white-space: nowrap;
                    }               
                }                    
            }
        }
    }          
}
.literacy .box .n-flex .n-flex:nth-child(2) p {
    border: 1px solid var(--result-light-blue);
    color: var(--result-light-blue);
}

.numeracy .box .n-flex .n-flex:nth-child(2) p {
    border: 1px solid var(--result-red);
    color: var(--result-red);
}

.chart-wrap {
  border: 1px solid #999;
  border-radius: 10px;
  padding: 20px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  .inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    font-weight: 500;
    gap: 5px;
  }
 .n-flex {
    gap: 0;
    align-items: center;
    text-align: center;
    p {
        padding-top: 28px;
        width: 80px;
      }
  }
}
.caution {
  li {
    position: relative;
    padding-left: 16px;
    font-size: 17px;
    &::before {
      content: '※';
      position: absolute;
      left: 0;
    }
  }
}

.numeracy .chart-wrap .n-flex p:nth-child(3) {
    width: auto;
    padding-top: 0;
}

.hidden-content {
    display: none;
}
</style>
